import { CellType, CellValueDetailedType } from 'hyperformula';
import { cloneDeep, get } from 'lodash';
import { ContentState, convertFromRaw, EditorState } from 'draft-js';
// @ts-ignore - valid import
import getUserLocale from 'get-user-locale';

// Utils
import {
    dateObjectHasTime,
    dateTimeStringHasSeconds,
    getDateFromString,
    toNumericDateTimeString,
} from '../../../../common/utils/timeUtil';
import { getDisplayValueOfCellContentString, parseCellContentString } from './tableInitialisationUtils';
import rawGetText from '../../../../common/utils/editor/rawUtils/rawGetText';
import hyperFormulaInstance from '../manager/hyperFormulaInstance';
import getContentInlineStyle from '../../../components/editor/customRichUtils/contentState/getContentInlineStyle';
import applyInlineStylesThroughoutContent from '../../../components/editor/customRichUtils/applyInlineStylesThroughoutContent';
import {
    getColorDisplayValue,
    getIsColorDarkerThan,
    mixColorsForOverlayEffect,
} from '../../../../common/colors/colorHelper';
import { getBackgroundColorClasses, isLightColor } from '../../utils/elementColorUtils';
import { getColumnCount, getRowCount } from './tableDataUtils';
import { getDefaultTypeObject, shouldUpdateCellType } from './tableCellTypeUtils';

// Types
import {
    CellData,
    CellTypeObject,
    CurrencyTypeObject,
    DateTimeTypeObject,
    NumberTypeObject,
    PercentageTypeObject,
} from '../../../../common/table/TableTypes';

// Constants
import {
    CellTypeConstants,
    CellTypeNames,
    CurrencyDisplayOptions,
    DateStringFormatOptions,
    FormatOptions,
    TextAlignment,
    TimeStringFormatOptions,
} from '../cellTypes/CellTypeConstants';
import {
    TABLE_CELL_DEFAULT_TEXT_ALIGNMENT,
    TABLE_DEFAULT_COLORS,
    TABLE_DEFAULT_HEADER_BACKGROUND_COLOR,
} from '../../../../common/table/tableConstants';
import { STYLE_COMMANDS } from '../../../components/editor/richText/richTextConstants';

/**************************
 * DATE/TIME
 **************************/

/**
 * Returns commonly used date cell information for a given string
 * @param str
 * @param type
 */
export const getDateTimeValuesFromInputString = (
    str: string | null | undefined,
    type?: DateTimeTypeObject | undefined,
): { epochTime: number; numericDateTimeString: string; hasTime: boolean; hasSeconds: boolean } | undefined => {
    if (!str) return;

    const date = getDateFromString(str);
    if (!date) return;

    const hasTime = type?.hasTime || dateObjectHasTime(date) || !!str.match(/\d{1,2}:\d{1,2}/);
    const hasSeconds = type?.hasSeconds || dateTimeStringHasSeconds(str);
    const numericDateTimeString = toNumericDateTimeString(date, hasTime, hasSeconds);
    return { epochTime: new Date(date).getTime(), numericDateTimeString, hasTime, hasSeconds };
};

/**
 * When changing the contents of a datetime cell, we need to update these display options
 * based on the input
 */
export const getDateFormattingOptions = (
    prevValue: string | null = null,
    prevTypeObject: CellTypeObject | undefined,
    newValue: string | number | null,
    newTypeObject: DateTimeTypeObject | undefined,
): Partial<DateTimeTypeObject> => {
    // If the existing type is not a datetime type, return
    if ((newTypeObject && newTypeObject.name !== CellTypeNames.DATE_TIME) || !newValue) return {};

    // Check if the previous cell type was also a datetime type
    const wasPreviouslyDate = prevTypeObject && prevTypeObject.name === CellTypeNames.DATE_TIME;

    const prevDate = prevValue ? new Date(Number(prevValue)) : prevValue;
    const prevHasSeconds = prevDate && wasPreviouslyDate && prevTypeObject?.[FormatOptions.HAS_SECONDS];
    const prevDateHasTime = prevDate && wasPreviouslyDate && prevTypeObject[FormatOptions.HAS_TIME];

    const newDate = new Date(Number(newValue));
    const newHasSeconds = newDate.getSeconds() || newTypeObject?.[FormatOptions.HAS_SECONDS];
    const newDateHasTime = dateObjectHasTime(newDate) || (newTypeObject && newTypeObject[FormatOptions.HAS_TIME]);

    // **************************************************
    // Check whether format options should be recalculated

    // Checking if hasSeconds or hasTime has changed
    const hasSecondsHasChanged = (prevHasSeconds && !newHasSeconds) || (!prevHasSeconds && newHasSeconds);
    const hasTimeHasChanged = (prevDateHasTime && !newDateHasTime) || (!prevDateHasTime && newDateHasTime);

    // If dates are the same, or neither hasSeconds nor hasTime has changed,
    // and it's not a new date, then return nothing
    if ((prevDate === newDate || (!hasSecondsHasChanged && !hasTimeHasChanged)) && wasPreviouslyDate) return {};

    // **************************************************
    // Calculate time format

    const { NONE, TWELVE_HOUR_SECONDS, TWELVE_HOUR, TWENTY_FOUR_HOUR, TWENTY_FOUR_HOUR_SECONDS } =
        TimeStringFormatOptions;

    // If the time has been removed, or it's a new date and there is no time, return formatting for none
    if (!newDateHasTime && (!wasPreviouslyDate || hasTimeHasChanged)) {
        return { [FormatOptions.HAS_TIME]: false, [FormatOptions.TIME_FORMAT]: NONE };
    }

    // Initial format which is either the existing or the default 12h format
    let timeFormat = wasPreviouslyDate ? prevTypeObject[FormatOptions.TIME_FORMAT] : TWELVE_HOUR;

    // you cant add a time with seconds unless the time format is none,
    // because otherwise the time is already there in the editor
    const timeHasBeenAdded = hasTimeHasChanged && newDateHasTime;
    const shouldGetNewFormat = hasSecondsHasChanged || (timeHasBeenAdded && timeFormat === NONE);

    // Update the format based if seconds have been added or removed
    if (shouldGetNewFormat) timeFormat = newHasSeconds ? TWELVE_HOUR_SECONDS : TWELVE_HOUR;

    // **************************************************
    // Create the format options object

    const dateFormattingOptions = { [FormatOptions.HAS_TIME]: true, [FormatOptions.TIME_FORMAT]: timeFormat };
    if (!wasPreviouslyDate) return dateFormattingOptions;

    // **************************************************
    // If there is a time component and user has previously selected 24h display, update the formatOptions to 24h

    if ([TWENTY_FOUR_HOUR, TWENTY_FOUR_HOUR_SECONDS].includes(prevTypeObject[FormatOptions.TIME_FORMAT])) {
        // Convert the time format to 24h
        dateFormattingOptions[FormatOptions.TIME_FORMAT] =
            timeFormat === TWELVE_HOUR_SECONDS ? TWENTY_FOUR_HOUR_SECONDS : TWENTY_FOUR_HOUR;
    }

    return dateFormattingOptions;
};

export const getFormattedDate = (date: Date, dateFormat: DateStringFormatOptions): string => {
    let formatOptions = {};
    switch (dateFormat) {
        case DateStringFormatOptions.WORDS_LONG:
            formatOptions = { dateStyle: 'long' };
            break;
        case DateStringFormatOptions.WORDS_SHORT:
            formatOptions = { dateStyle: 'medium' };
            break;
        case DateStringFormatOptions.NUMERIC_SHORT:
            formatOptions = { year: '2-digit', month: 'numeric', day: 'numeric' };
            break;
        case DateStringFormatOptions.WEEKDAY:
            formatOptions = { dateStyle: 'full' };
    }
    let dateString = new Intl.DateTimeFormat(getUserLocale(), formatOptions).format(date);

    // Replace slashes/dashes if necessary for the locale
    if (dateFormat === DateStringFormatOptions.NUMERIC_DASHED) dateString = dateString.replace(/\//g, '-');
    if (dateFormat === DateStringFormatOptions.NUMERIC_LONG || dateFormat === DateStringFormatOptions.NUMERIC_SHORT) {
        dateString = dateString.replace(/-/g, '/');
    }

    return dateString;
};

export const getFormattedTime = (date: Date, timeFormat: TimeStringFormatOptions): string => {
    let formatOptions = {};

    switch (timeFormat) {
        case TimeStringFormatOptions.NONE:
            return '';
        case TimeStringFormatOptions.TWELVE_HOUR_SECONDS:
            formatOptions = { hourCycle: 'h12', hour: 'numeric', minute: 'numeric', second: 'numeric' };
            break;
        case TimeStringFormatOptions.TWELVE_HOUR:
            formatOptions = { hourCycle: 'h12', hour: 'numeric', minute: 'numeric' };
            break;
        case TimeStringFormatOptions.TWENTY_FOUR_HOUR_SECONDS:
            formatOptions = { hourCycle: 'h23', hour: 'numeric', minute: 'numeric', second: 'numeric' };
            break;
        case TimeStringFormatOptions.TWENTY_FOUR_HOUR:
            formatOptions = { hourCycle: 'h23', timeStyle: 'short' };
            break;
    }
    return new Intl.DateTimeFormat(getUserLocale(), formatOptions).format(date);
};

/**
 * Returns a formatted date time string for rendering the cell
 */
export const getDateFormatting = (
    displayValue: string | number,
    value: string | null,
    type: DateTimeTypeObject,
): string => {
    if (!displayValue) return '';

    const date = getDateFromString(displayValue);
    if (!date) return String(displayValue);

    const { dateFormat, timeFormat, timeInBrackets } = type;
    const dateText = getFormattedDate(date, dateFormat);
    if (timeFormat === TimeStringFormatOptions.NONE) return dateText;

    const time = getFormattedTime(date, timeFormat);
    return `${dateText} ${timeInBrackets ? `(${time})` : `${time}`}`;
};

/**************************
 * CURRENCY
 **************************/

const commonCurrencySymbolsRegex = /(^[$€£¥₹₽₴₩₪]|[$€£¥₹₽₴₩₪]$)/;

export const getCurrencyDisplayName = (currencyCode: string): string => {
    if (!currencyCode) return '';
    // @ts-ignore - valid property of Intl
    return new Intl.DisplayNames(getUserLocale(), { type: 'currency' }).of(currencyCode);
};

export const showCurrencyDisplay = (displayOption: string, currency: string): string =>
    new Intl.NumberFormat(getUserLocale(), {
        style: 'currency',
        currency: currency,
        currencyDisplay: displayOption,
    }).format(12345);

export const getCurrencyDisplayOptions = (currency: string): Array<{ option: string; value: string } | undefined> => {
    const values: string[] = [];
    const displayOptions = Object.values(CurrencyDisplayOptions).map((option) => {
        const value = showCurrencyDisplay(option, currency);
        if (values.includes(value)) return;

        values.push(value);
        return { option, value };
    });
    // filter out undefined values
    return displayOptions.filter((option) => option);
};

export const getTableCurrencyList = (): Array<string> => {
    // @ts-ignore - valid property of Intl
    const supportedCurrencyCodes = Intl.supportedValuesOf('currency');
    return supportedCurrencyCodes.sort((a: string, b: string) => {
        if (getCurrencyDisplayName(a) < getCurrencyDisplayName(b)) {
            return -1;
        }
        if (getCurrencyDisplayName(a) > getCurrencyDisplayName(b)) {
            return 1;
        }
        return 0;
    });
};

/**************************
 * NUMBER
 **************************/

const validNumberRegex = /^[0-9]{0,3}(,[0-9]{3})*(\.[0-9]*)?$/;
const validPercentageRegex = /%$/;

/**
 * Removes any numeric symbols from a string such as the currency symbol, thousands separators, etc.
 * this way it can be converted to a number
 */
const removeNumberSymbols = (str: string): string => {
    let parsedString = str;
    parsedString = parsedString.replace(validPercentageRegex, '');
    parsedString = parsedString.replace(commonCurrencySymbolsRegex, '');
    parsedString = parsedString.replaceAll(/\s/g, '');

    const isValidNumber = parsedString.match(validNumberRegex);
    if (isValidNumber) parsedString = parsedString.replaceAll(',', '');

    return parsedString;
};

/**
 * Returns the decimal options to use when formatting a number using the Intl.NumberFormat object
 * If the user has set the number of decimals, use that number as both max and min to force that number of
 * decimal places, otherwise calculate the number of decimal places in the input so that we override the default number
 * and show the full number of decimal places entered
 */
const getDecimalOptions = (numberValue: number | string, decimals: number | string) => {
    // If user has chosen auto or decimals is not a valid number, use the number of decimal places in the input value
    const useInputDecimalPlaces = decimals === 'auto' || isNaN(Number(decimals));
    const inputDecimalPlaces = numberValue.toString().split('.')[1]?.length || 0;

    const decimalsValue = useInputDecimalPlaces ? inputDecimalPlaces : decimals;
    return { maximumFractionDigits: decimalsValue, minimumFractionDigits: decimalsValue };
};

/**
 * Checks the input string to see if it should be formatted as a number,
 * returns the type of number it should be formatted as and the new value
 */
export const stringShouldFormatAsType = (
    str: string,
):
    | {
          newCellType:
              | CellTypeNames.PERCENTAGE
              | CellTypeNames.CURRENCY
              | CellTypeNames.NUMBER
              | CellTypeNames.DATE_TIME;
          newValue: number | string;
          otherTypeOptions?: Partial<CellTypeObject>;
      }
    | undefined => {
    // Percentage
    const percentageNumber = Number(removeNumberSymbols(str));
    const valueEndsWithPercentage = str.match(validPercentageRegex) && !isNaN(percentageNumber);
    if (valueEndsWithPercentage)
        return {
            newCellType: CellTypeNames.PERCENTAGE,
            newValue: str,
        };

    // Currency
    const valueContainsCurrencySymbol = str.match(commonCurrencySymbolsRegex);
    const currencyNumber = Number(removeNumberSymbols(str));
    const valueIsCurrencyNumber = !isNaN(Number(currencyNumber));
    if (valueContainsCurrencySymbol && valueIsCurrencyNumber)
        return { newCellType: CellTypeNames.CURRENCY, newValue: currencyNumber };

    // Number
    const numberValue = Number(removeNumberSymbols(str));
    const valueIsNumber = !isNaN(numberValue);
    if (valueIsNumber) return { newCellType: CellTypeNames.NUMBER, newValue: numberValue };

    // Date
    const dateFormatValues = getDateTimeValuesFromInputString(str);
    if (dateFormatValues) {
        const { numericDateTimeString, hasTime, hasSeconds } = dateFormatValues;
        return {
            newCellType: CellTypeNames.DATE_TIME,
            newValue: numericDateTimeString,
            otherTypeOptions: { hasTime, hasSeconds },
        };
    }
};

export const getNumberFormatting = (
    value: number | string,
    type: NumberTypeObject | CurrencyTypeObject | PercentageTypeObject,
): string | number => {
    const { name, decimals, thousandsSeparator } = type;

    let numberValue = Number(removeNumberSymbols(String(value)));

    // If the cell value is not numeric, return value
    if (isNaN(numberValue)) return value;

    // If the value is in a percentage format, we need to convert it to a number before formatting
    const isPercentageValue = typeof value === 'string' && value.match(validPercentageRegex);
    if (isPercentageValue) {
        numberValue = numberValue / 100;
    }

    // Shared option values, this is all we need for Number format
    let options: { [key: string]: string | number | boolean } = {
        useGrouping: thousandsSeparator,
        ...getDecimalOptions(numberValue, decimals),
    };

    // Add additional options for currency and percentage
    switch (name) {
        case CellTypeNames.CURRENCY:
            options = {
                ...options,
                style: 'currency',
                currency: type.currency || 'USD',
                currencyDisplay: type.currencyDisplay || CurrencyDisplayOptions.NARROW_SYMBOL,
                currencySign: type.accounting ? 'accounting' : 'standard',
            };
            break;

        case CellTypeNames.PERCENTAGE:
            options = {
                ...options,
                style: 'percent',
            };
            break;
    }
    return new Intl.NumberFormat(getUserLocale(), options).format(numberValue);
};

/**************************
 * TABLE CELL RENDERING
 **************************/

/**
 * Convert render value calculated by HyperFormula to a more readable value.
 *
 * This covers cases such as:
 * - If formula is calculated to be a date by HyperFormula, it will be represented in a different format (numbers of
 *   days from X date), we want to render this in a more readable way.
 */
export const getCellFormulaRenderValue = (
    hotRenderValue: string | number,
    options: { hfType?: string; hfDetailedType?: string } = {},
): string | number => {
    const { hfType, hfDetailedType } = options;

    if (
        typeof hotRenderValue === 'number' &&
        hfType === CellType.FORMULA &&
        (hfDetailedType === CellValueDetailedType.NUMBER_DATE ||
            hfDetailedType === CellValueDetailedType.NUMBER_DATETIME)
    ) {
        const dateTime = hyperFormulaInstance.numberToDateTime(hotRenderValue);
        const hasDate = 'year' in dateTime;
        const hasTime = 'hours' in dateTime;

        let date;
        if (hasDate && hasTime) {
            const { year, month, day, hours, minutes, seconds } = dateTime;
            date = new Date(year, month - 1, day, hours, minutes, seconds);
        } else if (hasDate) {
            const { year, month, day } = dateTime;
            date = new Date(year, month - 1, day);
        } else {
            return hotRenderValue;
        }

        const epochTime = date.getTime().toString();
        const dateTypeObject = {
            ...getDefaultTypeObject<DateTimeTypeObject>(CellTypeNames.DATE_TIME),
            hasTime: hfDetailedType === CellValueDetailedType.NUMBER_DATETIME,
        };

        const values = getDateTimeValuesFromInputString(epochTime, dateTypeObject);
        if (values) return values.numericDateTimeString;
    }

    return hotRenderValue;
};

export const getCellTypeRenderValue = (
    cellData: CellData,
    hotRenderValue: string | number,
    options: { hfType?: string; hfDetailedType?: string } = {},
): string | number => {
    const { value, type } = cellData;

    const renderValue = getDisplayValueOfCellContentString(hotRenderValue);
    const normalisedRenderValue = getCellFormulaRenderValue(renderValue, options);

    if (!type || !normalisedRenderValue) return normalisedRenderValue;

    switch (type.name) {
        case CellTypeNames.TEXT:
            return normalisedRenderValue;
        case CellTypeNames.DATE_TIME:
            return getDateFormatting(normalisedRenderValue, value, type);
        case CellTypeNames.CURRENCY:
        case CellTypeNames.PERCENTAGE:
        case CellTypeNames.NUMBER:
            return getNumberFormatting(normalisedRenderValue, type);
        default:
            return normalisedRenderValue;
    }
};

export const getCellRenderContentState = (
    cellData: CellData,
    hotRenderValue?: string | number | null,
    options: { hfType?: string; hfDetailedType?: string } = {},
): ContentState => {
    if (!hotRenderValue) return EditorState.createEmpty().getCurrentContent();

    const parsedContentString = parseCellContentString(hotRenderValue);
    const renderValue =
        typeof parsedContentString === 'object' ? (rawGetText(parsedContentString) as string) : parsedContentString;

    // Get the render value for different cell types
    const mnRenderValue = getCellTypeRenderValue(cellData, renderValue, options);

    // if the value is in draftJS format and is unchanged, use the original value
    const contentState =
        typeof parsedContentString === 'object' && renderValue === mnRenderValue
            ? convertFromRaw(parsedContentString)
            : ContentState.createFromText(mnRenderValue.toString());

    return applyInlineStylesThroughoutContent(contentState, cellData.textStyle);
};

/**
 * Returns the alignment for a cell based on the text content, otherwise gets
 * the default alignment for the given cell type, or defaults to left
 *
 * NOTE: alignmentOverride will only be applied if the cell type is not text
 */
export const getCellTextAlignment = (
    cellData?: CellData,
    hotRenderValue?: string | boolean | number | null,
): TextAlignment => {
    if (!cellData) return TABLE_CELL_DEFAULT_TEXT_ALIGNMENT;

    const { value, type, textAlignment } = cellData;

    if (textAlignment) return textAlignment;

    const cellTypeName = type?.name || CellTypeNames.AUTO;
    const cellTypeDefaultAlignment =
        (CellTypeConstants[cellTypeName].alignment as TextAlignment) || TABLE_CELL_DEFAULT_TEXT_ALIGNMENT;

    // If the cell is empty or is of cell type text, set alignment based on the cell type default
    if (!value || !hotRenderValue || cellTypeName === CellTypeNames.TEXT) return cellTypeDefaultAlignment;

    // Formula results
    if (hotRenderValue && typeof hotRenderValue === 'number') return TextAlignment.RIGHT;
    if (hotRenderValue && typeof hotRenderValue === 'boolean') return TextAlignment.LEFT;

    // Strings that should be formatted as numbers
    const text = getDisplayValueOfCellContentString(value);
    const { newCellType } = stringShouldFormatAsType(String(text)) || {};

    // return alignment for the new cell type only if there is a new cell type AND
    // current cell type is auto OR new cell type is a valid change from current cell type
    // OR new cell type is the same as current cell type
    if (newCellType && (!type || shouldUpdateCellType(newCellType, type.name) || newCellType === type.name)) {
        return CellTypeConstants[newCellType].alignment as TextAlignment;
    }

    // Text content is a string, align left
    const renderValue = getCellTypeRenderValue(cellData, hotRenderValue);
    if (isNaN(Number(renderValue))) return TextAlignment.LEFT;

    // Align based on cell type, or default to left
    return cellTypeDefaultAlignment;
};

/**************************
 * BACKGROUND AND FORMATTING
 **************************/

export const getCellBackgroundColor = (cellData: CellData): string => {
    const { background = null } = cellData || {};

    const colorValue = getColorDisplayValue(background);

    // if hex value, return it
    if (colorValue && colorValue.match(/^#([0-9a-f]{3}){1,2}$/i)) return colorValue;

    // if the default grey, return the variable
    if (background === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return 'var(--ws-table-header-row-background)';

    // if no value, return default element color value
    return 'var(--ws-element-background-primary)';
};

export const getBackgroundColorClass = (cellData: CellData): {} | undefined => {
    const { background = null } = cellData || {};
    if (!background) return;

    if (background === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return { 'colored-background': true };

    return getBackgroundColorClasses(background);
};

/**
 * This function determines the background and text style for a new cell based on the neighbouring cells
 * Returns a partial cell data object with the background and text style
 */
const getCellFormatFromNeighbours = (
    row: number,
    col: number,
    dataArray: Array<Array<CellData>>,
    numberAdded: number,
    isAddingRows: boolean,
): Partial<CellData> => {
    const twoBefore = isAddingRows ? { row: row - 2, col } : { row, col: col - 2 };
    const oneBefore = isAddingRows ? { row: row - 1, col } : { row, col: col - 1 };
    const oneAfter = isAddingRows ? { row: row + numberAdded, col } : { row, col: col + numberAdded };
    const twoAfter = isAddingRows ? { row: row + numberAdded + 1, col } : { row, col: col + numberAdded + 1 };

    const twoBeforeCell = get(dataArray, [twoBefore.row, twoBefore.col]);
    const oneBeforeCell = get(dataArray, [oneBefore.row, oneBefore.col]);
    const oneAfterCell = get(dataArray, [oneAfter.row, oneAfter.col]);
    const twoAfterCell = get(dataArray, [twoAfter.row, twoAfter.col]);

    const nRows = getRowCount(dataArray);
    const nCols = getColumnCount(dataArray);

    const previousCount = isAddingRows ? nRows - numberAdded : nCols - numberAdded;

    // If both before cells have the same background color, the first before and first after cells have the same
    // background color, or there was previously only one column, apply it to the new cell
    if (
        oneBeforeCell &&
        (twoBeforeCell?.background === oneBeforeCell.background ||
            oneBeforeCell.background === oneAfterCell?.background ||
            previousCount === 1)
    ) {
        return { background: oneBeforeCell.background, textStyle: oneBeforeCell.textStyle };
    }

    // If both after cells have the same background color, apply it to the new cell
    if (oneAfterCell && oneAfterCell?.background === twoAfterCell?.background) {
        return { background: oneAfterCell.background, textStyle: oneAfterCell.textStyle };
    }

    return {};
};

/**
 * This function checks cells nearby to the newly added cells to determine if they should have the same
 * background color or text styling
 * This function also updates hotTable if instance passed
 */
export const continueCellFormatting = (args: {
    dataArray: Array<Array<CellData>>;
    colIndex: number;
    nColsAdded: number;
    rowIndex: number;
    nRowsAdded: number;
    hotTableInstanceRef?: any;
}): Array<Array<CellData>> => {
    const { dataArray, colIndex, nColsAdded, rowIndex, nRowsAdded, hotTableInstanceRef } = args;
    const newData = cloneDeep(dataArray);

    const nRows = getRowCount(newData);
    const nCols = getColumnCount(newData);
    if (nColsAdded > 0) {
        for (let row = 0; row < nRows; row++) {
            for (let col = colIndex; col < nCols; col++) {
                // Add the new styles, but don't override any existing styles
                const newCellData = {
                    ...getCellFormatFromNeighbours(row, col, newData, nColsAdded, false),
                    ...newData[row][col],
                };
                newData[row][col] = newCellData;
                if (hotTableInstanceRef?.current)
                    hotTableInstanceRef.current?.setCellMetaObject(row, col, { cellData: newCellData });
            }
        }
    }
    if (nRowsAdded > 0) {
        for (let row = rowIndex; row < nRows; row++) {
            for (let col = 0; col < nCols; col++) {
                // Add the new styles, but don't override any existing styles
                const newCellData = {
                    ...getCellFormatFromNeighbours(row, col, newData, nRowsAdded, true),
                    ...newData[row][col],
                };
                newData[row][col] = newCellData;
                if (hotTableInstanceRef?.current)
                    hotTableInstanceRef.current?.setCellMetaObject(row, col, { cellData: newCellData });
            }
        }
    }
    return newData;
};

/**
 * This function takes in a color name and returns the hex value of the color
 */
export const getBackgroundColorHex = (color: string, isDarkMode: boolean): string => {
    // these are the light and dark values of --ws-element-background-primary
    if (!color) return isDarkMode ? '#333333' : '#ffffff';

    // these are the light and dark values of --ws-table-header-row-background
    if (color === TABLE_DEFAULT_HEADER_BACKGROUND_COLOR) return isDarkMode ? '#4D4D4D' : '#D2D3D6';

    // @ts-ignore - if it's not a valid key then we just return the color value
    return TABLE_DEFAULT_COLORS[color]?.hex || color;
};

/**
 * This function takes in cell coords and determines if the border should overlap the current cell
 * returns { color, shouldOverlapCurrentCell }
 */
const getNeighbourCellColorInfo = (
    row: number,
    col: number,
    currentColor: string,
    instance: any,
): { color: string | null; shouldOverlapCurrentCell: boolean } => {
    if (row >= instance.countRows() || row < 0 || col >= instance.countCols() || col < 0)
        return { color: null, shouldOverlapCurrentCell: false };

    const cellData = instance.getCellMeta(row, col)?.cellData;
    if (!cellData || !cellData.background) return { color: null, shouldOverlapCurrentCell: false };

    const isDarkMode = instance.milanoteProps.isDarkMode || false;
    const hexColor = getBackgroundColorHex(cellData.background, isDarkMode);

    const isDarker = cellData.background && getIsColorDarkerThan(hexColor, currentColor);

    // should show border for neighbour cell instead of current cell if neighbouring cell is darker
    // than current cell in light mode, or lighter in dark mode
    return {
        color: hexColor,
        shouldOverlapCurrentCell: (isDarkMode ? !isDarker : isDarker) && !!hexColor,
    };
};

/**
 * if cell has a background color, set the renderer border color so that we see border on the top and left edges
 */
export const colorBorders = (instance: any, row: number, col: number) => {
    const cellData = instance?.getCellMeta(row, col)?.cellData;
    if (!cellData?.background || !instance) return;

    const hexColor = getBackgroundColorHex(cellData.background, instance.milanoteProps.isDarkMode || false);

    // Calculate the new border colour that looks like a white/grey opacity over the background colour
    const defaultBorderColor = isLightColor(hexColor) ? 'rgb(50, 59, 74)' : 'rgb(255, 255, 255)';
    const borderColor = mixColorsForOverlayEffect(hexColor, defaultBorderColor).toString();

    // Check neighbouring cells for color
    const cellAbove = getNeighbourCellColorInfo(row - 1, col, hexColor, instance);
    const shouldColorTopBorder = !cellAbove.shouldOverlapCurrentCell;

    const cellLeft = getNeighbourCellColorInfo(row, col - 1, hexColor, instance);
    const shouldColorLeftBorder = !cellLeft.shouldOverlapCurrentCell && col !== 0;

    // ********** Set values in the dom **********

    // Wrap in requestAnimationFrame to ensure that the contents of the cell has been rendered properly
    requestAnimationFrame(() => {
        // Get the TD and cell renderer elements
        const td = instance.getCell(row, col);
        if (!td) return;

        const renderer = td.querySelector('.MilanoteCellRendererComponent');
        if (!renderer) return;

        // remove existing border classes
        renderer.classList.remove(
            'colored-border-top-only',
            'colored-border-left-only',
            'colored-no-border-top-right-corner',
            'colored-border-bottom-right-corner',
        );

        // set the td border color so that it matches if we use a border colour that is different to the standard border colour
        // e.g. a dark coloured cell will have a light border even in light mode
        td.style.borderColor = 'transparent';
        if (col !== instance.countCols() - 1) td.style.borderRightColor = borderColor;
        td.style.borderBottomColor = borderColor;

        renderer.style.borderColor = 'transparent';
        if (shouldColorTopBorder && shouldColorLeftBorder) {
            renderer.style.borderColor = borderColor;
        } else if (shouldColorTopBorder) {
            renderer.style.borderTopColor = borderColor;
            renderer.classList.add('colored-border-top-only');
        } else if (shouldColorLeftBorder) {
            renderer.style.borderLeftColor = borderColor;
            renderer.classList.add('colored-border-left-only');
        }

        // ********** add extra classes for fixing overlapping corners **********
        // By adjusting the top right and bottom right corners on each cell, we can set the colour
        // of all the corners of the table so that they look like they are overlapping in the correct way

        // add a class so that we DON'T cover the top right corner of the current cell
        const cellTopRight = getNeighbourCellColorInfo(row - 1, col + 1, hexColor, instance);
        if (cellTopRight.shouldOverlapCurrentCell) {
            renderer.classList.add('colored-no-border-top-right-corner');
        }

        // add a class so that we DO cover the bottom right corner of the current cell
        const cellBottomRight = getNeighbourCellColorInfo(row + 1, col + 1, hexColor, instance);
        if (!!cellBottomRight.color && !cellBottomRight.shouldOverlapCurrentCell) {
            renderer.classList.add('colored-border-bottom-right-corner');
        }
    });
};

/**
 * This function returns all the text styling that are applied to the whole text inside a cell
 *
 * NOTE: Will exclude styles that are not supported by table cells, or cannot be applied to
 *       cell text to using toolbar
 */
export const getCellLevelTextStyles = (content: ContentState): string[] => {
    return Array.from(getContentInlineStyle(content) || []).filter(
        (style) =>
            ![
                STYLE_COMMANDS.LARGE_HEADING,
                STYLE_COMMANDS.HEADING,
                STYLE_COMMANDS.SMALL_TEXT,
                STYLE_COMMANDS.CODE,
                STYLE_COMMANDS.BLOCKQUOTE,
            ].includes(style),
    );
};
